import React from "react";

import Autocomplete from "@material-ui/lab/Autocomplete";
import PropTypes from "prop-types";

import RbTextControl from "../RbTextControl";
import useChipsStyles from "./useChipsStyles";
import useRbAutocompleteControlStyles from "./useRbAutocompleteControlStyles";

const RbAutocompleteControl = React.forwardRef(function RbAutocompleteControl(
  props,
  ref
) {
  const {
    autoComplete,
    autoHighlight,
    autoSelect,
    blurOnSelect,
    ChipProps,
    classes: classesProp,
    className,
    clearOnBlur,
    clearOnEscape,
    clearText,
    closeIcon,
    closeText,
    debug,
    defaultValue,
    disableClearable,
    disableCloseOnSelect,
    disabled,
    disabledItemsFocusable,
    disableListWrap,
    disablePortal,
    filterOptions,
    filterSelectedOptions,
    forcePopupIcon,
    freeSolo,
    fullWidth,
    getLimitTagsText,
    getOptionDisabled,
    getOptionLabel,
    getOptionSelected,
    groupBy,
    handleHomeEndKeys,
    id,
    includeInputInList,
    inputValue,
    limitTags,
    ListboxComponent,
    ListboxProps,
    loading,
    loadingText,
    multiple,
    noOptionsText,
    onChange,
    onChangeValue,
    onClose,
    onHighlightChange,
    onInputChange,
    onOpen,
    open,
    openOnFocus,
    openText,
    options,
    PaperComponent,
    PopperComponent,
    popupIcon,
    renderGroup,
    renderOption,
    renderTags,
    selectOnFocus,
    size,
    value,
    ...other
  } = props;

  const classes = useRbAutocompleteControlStyles(props);
  const chipClasses = useChipsStyles();

  const handleChange = React.useCallback(
    (event, targetValue) => {
      if (onChange) {
        onChange(event, targetValue);
      }
      if (onChangeValue) {
        const empty = !targetValue || targetValue.length === 0;
        onChangeValue(empty ? undefined : targetValue);
      }
    },
    [onChange, onChangeValue]
  );

  return (
    <Autocomplete
      autoComplete={autoComplete}
      autoHighlight={autoHighlight}
      autoSelect={autoSelect}
      blurOnSelect={blurOnSelect}
      ChipProps={{ size: "small", classes: chipClasses, ...ChipProps }}
      classes={classes}
      className={className}
      clearOnBlur={clearOnBlur}
      clearOnEscape={clearOnEscape}
      clearText={clearText}
      closeIcon={closeIcon}
      closeText={closeText}
      debug={debug}
      defaultValue={defaultValue}
      disableClearable={disableClearable}
      disableCloseOnSelect={disableCloseOnSelect}
      disabled={disabled}
      disabledItemsFocusable={disabledItemsFocusable}
      disableListWrap={disableListWrap}
      disablePortal={disablePortal}
      filterOptions={filterOptions}
      filterSelectedOptions={filterSelectedOptions}
      forcePopupIcon={forcePopupIcon}
      freeSolo={freeSolo}
      fullWidth={fullWidth}
      getLimitTagsText={getLimitTagsText}
      getOptionDisabled={getOptionDisabled}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      groupBy={groupBy}
      handleHomeEndKeys={handleHomeEndKeys}
      id={id}
      includeInputInList={includeInputInList}
      inputValue={inputValue}
      limitTags={limitTags}
      ListboxComponent={ListboxComponent}
      ListboxProps={ListboxProps}
      loading={loading}
      loadingText={loadingText}
      multiple={multiple}
      noOptionsText={noOptionsText}
      onChange={handleChange}
      onClose={onClose}
      onHighlightChange={onHighlightChange}
      onInputChange={onInputChange}
      onOpen={onOpen}
      open={open}
      openOnFocus={openOnFocus}
      openText={openText}
      options={options}
      PaperComponent={PaperComponent}
      PopperComponent={PopperComponent}
      popupIcon={popupIcon}
      renderGroup={renderGroup}
      renderOption={renderOption}
      renderTags={renderTags}
      selectOnFocus={selectOnFocus}
      size={size}
      value={value}
      renderInput={(params) => {
        return <RbTextControl ref={ref} {...params} {...other} />;
      }}
    />
  );
});

function defaultGetOptionSelected(option, value) {
  return option.id === value.id;
}

function defaultGetOptionLabel(option) {
  return option.name ?? option.description ?? "";
}

RbAutocompleteControl.propTypes = {
  autoComplete: PropTypes.bool,
  autoHighlight: PropTypes.bool,
  autoSelect: PropTypes.bool,
  blurOnSelect: PropTypes.oneOfType([
    PropTypes.oneOf(["mouse", "touch"]),
    PropTypes.bool,
  ]),
  ChipProps: PropTypes.object,
  classes: PropTypes.object,
  className: PropTypes.string,
  clearOnBlur: PropTypes.bool,
  clearOnEscape: PropTypes.bool,
  clearText: PropTypes.string,
  closeIcon: PropTypes.node,
  closeText: PropTypes.string,
  debug: PropTypes.bool,
  defaultValue: PropTypes.any,
  disableClearable: PropTypes.bool,
  disableCloseOnSelect: PropTypes.bool,
  disabled: PropTypes.bool,
  disabledItemsFocusable: PropTypes.bool,
  disableListWrap: PropTypes.bool,
  disablePortal: PropTypes.bool,
  filterOptions: PropTypes.func,
  filterSelectedOptions: PropTypes.bool,
  forcePopupIcon: PropTypes.oneOfType([
    PropTypes.oneOf(["auto"]),
    PropTypes.bool,
  ]),
  freeSolo: PropTypes.bool,
  fullWidth: PropTypes.bool,
  getLimitTagsText: PropTypes.func,
  getOptionDisabled: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionSelected: PropTypes.func,
  groupBy: PropTypes.func,
  handleHomeEndKeys: PropTypes.bool,
  id: PropTypes.string,
  includeInputInList: PropTypes.bool,
  inputValue: PropTypes.string,
  limitTags: PropTypes.number,
  ListboxComponent: PropTypes.elementType,
  ListboxProps: PropTypes.object,
  loading: PropTypes.bool,
  loadingText: PropTypes.node,
  multiple: PropTypes.bool,
  noOptionsText: PropTypes.node,
  onChange: PropTypes.func,
  onChangeValue: PropTypes.func,
  onClose: PropTypes.func,
  onHighlightChange: PropTypes.func,
  onInputChange: PropTypes.func,
  onOpen: PropTypes.func,
  open: PropTypes.bool,
  openOnFocus: PropTypes.bool,
  openText: PropTypes.string,
  options: PropTypes.array.isRequired,
  PaperComponent: PropTypes.elementType,
  PopperComponent: PropTypes.elementType,
  popupIcon: PropTypes.node,
  renderGroup: PropTypes.func,
  renderOption: PropTypes.func,
  renderTags: PropTypes.func,
  selectOnFocus: PropTypes.bool,
  size: PropTypes.oneOf(["medium", "small"]),
  value: PropTypes.any,
};

RbAutocompleteControl.defaultProps = {
  autoComplete: undefined,
  autoHighlight: undefined,
  autoSelect: undefined,
  blurOnSelect: undefined,
  ChipProps: undefined,
  classes: undefined,
  className: undefined,
  clearOnBlur: undefined,
  clearOnEscape: undefined,
  clearText: undefined,
  closeIcon: undefined,
  closeText: undefined,
  debug: undefined,
  defaultValue: undefined,
  disableClearable: undefined,
  disableCloseOnSelect: undefined,
  disabled: undefined,
  disabledItemsFocusable: undefined,
  disableListWrap: undefined,
  disablePortal: undefined,
  filterOptions: undefined,
  filterSelectedOptions: undefined,
  forcePopupIcon: undefined,
  freeSolo: undefined,
  fullWidth: undefined,
  getLimitTagsText: undefined,
  getOptionDisabled: undefined,
  getOptionLabel: defaultGetOptionLabel,
  getOptionSelected: defaultGetOptionSelected,
  groupBy: undefined,
  handleHomeEndKeys: undefined,
  id: undefined,
  includeInputInList: undefined,
  inputValue: undefined,
  limitTags: undefined,
  ListboxComponent: undefined,
  ListboxProps: undefined,
  loading: undefined,
  loadingText: undefined,
  multiple: undefined,
  noOptionsText: undefined,
  onChange: undefined,
  onChangeValue: undefined,
  onClose: undefined,
  onHighlightChange: undefined,
  onInputChange: undefined,
  onOpen: undefined,
  open: undefined,
  openOnFocus: undefined,
  openText: undefined,
  PaperComponent: undefined,
  PopperComponent: undefined,
  popupIcon: undefined,
  renderGroup: undefined,
  renderOption: undefined,
  renderTags: undefined,
  selectOnFocus: undefined,
  size: undefined,
  value: undefined,
};

export default React.memo(RbAutocompleteControl);
